import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, SlidersHorizontal, X } from 'lucide-react'
import { FilterSelect } from './FilterSelect'
import { FilterInput } from './FilterInput'
import { FilterCheckboxGroup } from './FilterCheckboxGroup'
import { Brand, Model, Version } from '../../types'
import { VehicleOptions } from '../../hooks/useVehicleOptions'
import { cn } from '../../lib/utils'

export interface VehicleFiltersData {
  search: string
  status: string
  condition: string
  category: string
  brandId: string
  modelId: string
  versionId: string
  minYear: string
  maxYear: string
  minMileage: string
  maxMileage: string
  fuelType: string
  plateEnd: string
  armorLevel: string
  color: string
  type: string
  doors: string
  isAuction: string
  specialCategory: string
  characteristics: string[]
}

interface VehicleFiltersProps {
  filters: VehicleFiltersData
  onFilterChange: (filters: VehicleFiltersData) => void
  onClearFilters: () => void
  activeCount: number
  brands: Brand[]
  models: Model[]
  versions: Version[]
  options: VehicleOptions
}

interface FilterSectionProps {
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
}

const FilterSection: React.FC<FilterSectionProps> = ({
  title,
  children,
  defaultOpen = true,
}) => {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
      >
        {title}
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 transition-transform duration-200',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && <div className="px-4 pb-4 space-y-2">{children}</div>}
    </div>
  )
}

// Status - uses API options via props.options.statuses
const getStatusOptions = (options: VehicleOptions) => {
  return options.statuses?.length > 0 
    ? options.statuses 
    : [
        { value: 'in_stock', label: 'Em Estoque' },
        { value: 'reserved', label: 'Reservado' },
        { value: 'sold', label: 'Vendido' },
        { value: 'in_transit', label: 'Em Trânsito' },
      ]
}

// Condition - uses API options via props.options.conditions
const getConditionOptions = (options: VehicleOptions) => {
  return options.conditions?.length > 0 
    ? options.conditions 
    : [
        { value: 'new_vehicle', label: '0 KM' },
        { value: 'semi_new', label: 'Semi-novo' },
        { value: 'used', label: 'Usado' },
      ]
}

// Category - uses API options via props.options.categories
const getCategoryOptions = (options: VehicleOptions) => {
  if (options.categories?.length > 0) {
    return options.categories
  }
  return [
    { value: 'A', label: 'Moto' },
    { value: 'B', label: 'Carro' },
    { value: 'C', label: 'Caminhão' },
    { value: 'D', label: 'Ônibus' },
    { value: 'E', label: 'Reboque' },
  ]
}

export const VehicleFilters: React.FC<VehicleFiltersProps> = ({
  filters,
  onFilterChange,
  onClearFilters,
  activeCount,
  brands,
  models,
  versions,
  options,
}) => {
  const { t } = useTranslation()
  const updateFilter = (key: keyof VehicleFiltersData, value: any) => {
    const newFilters = { ...filters, [key]: value }
    if (key === 'category') {
      newFilters.brandId = ''
      newFilters.modelId = ''
      newFilters.versionId = ''
    }
    if (key === 'brandId') {
      newFilters.modelId = ''
      newFilters.versionId = ''
    }
    if (key === 'modelId') {
      newFilters.versionId = ''
    }
    onFilterChange(newFilters)
  }

  const currentYear = new Date().getFullYear()

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden shadow-precision">
      {/* Panel header */}
      <div className="flex items-center justify-between px-4 py-3 bg-surface border-b border-border">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-accent" />
          <span className="text-sm font-semibold text-foreground">Filtros</span>
          {activeCount > 0 && (
            <span className="inline-flex items-center justify-center h-4 min-w-4 px-1 text-[10px] font-bold rounded-full bg-accent text-white">
              {activeCount}
            </span>
          )}
        </div>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={onClearFilters}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-accent transition-colors"
          >
            <X className="h-3 w-3" />
            Limpar
          </button>
        )}
      </div>

      {/* Status */}
      <FilterSection title={t('vehicles.table.status')}>
        <div className="grid grid-cols-2 gap-1.5">
          {getStatusOptions(options).map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() =>
                updateFilter('status', filters.status === opt.value ? '' : opt.value)
              }
              className={cn(
                'text-xs font-medium px-2 py-1.5 rounded-md border transition-all text-left',
                filters.status === opt.value
                  ? 'bg-accent text-white border-accent'
                  : 'bg-card text-foreground border-border hover:border-accent/40 hover:bg-surface',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </FilterSection>

      {/* Condição */}
      <FilterSection title={t('vehicles.fields.condition')}>
        <div className="flex flex-col gap-1">
          {getConditionOptions(options).map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() =>
                updateFilter('condition', filters.condition === opt.value ? '' : opt.value)
              }
              className={cn(
                'text-xs font-medium px-3 py-2 rounded-md border transition-all text-left',
                filters.condition === opt.value
                  ? 'bg-accent/10 text-accent border-accent/30'
                  : 'bg-card text-foreground border-transparent hover:bg-surface hover:border-border',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </FilterSection>

      {/* Marca e Modelo */}
      <FilterSection title="Marca e Modelo">
        <FilterSelect
          label="Marca"
          value={filters.brandId}
          onChange={(value) => updateFilter('brandId', value)}
          options={brands.map((b) => ({ value: b.id, label: b.name }))}
          placeholder="Todas as marcas"
        />
        <FilterSelect
          label="Modelo"
          value={filters.modelId}
          onChange={(value) => updateFilter('modelId', value)}
          options={models.map((m) => ({ value: m.id, label: m.name }))}
          placeholder="Todos os modelos"
          disabled={!filters.brandId}
        />
        <FilterSelect
          label="Versão"
          value={filters.versionId}
          onChange={(value) => updateFilter('versionId', value)}
          options={versions.map((v) => ({ value: v.id, label: v.name }))}
          placeholder="Todas as versões"
          disabled={!filters.modelId}
        />
      </FilterSection>

      {/* Categoria */}
      <FilterSection title={t('vehicles.category')} defaultOpen={false}>
        <FilterSelect
          label=""
          value={filters.category}
          onChange={(value) => updateFilter('category', value)}
          options={getCategoryOptions(options)}
          placeholder="Todas"
        />
      </FilterSection>

      {/* Ano */}
      <FilterSection title="Ano" defaultOpen={false}>
        <div className="grid grid-cols-2 gap-2">
          <FilterInput
            label="De"
            type="number"
            value={filters.minYear}
            onChange={(value) => updateFilter('minYear', value)}
            placeholder={String(currentYear - 5)}
            min="1900"
            max={currentYear + 1}
          />
          <FilterInput
            label="Até"
            type="number"
            value={filters.maxYear}
            onChange={(value) => updateFilter('maxYear', value)}
            placeholder={String(currentYear + 1)}
            min="1900"
            max={currentYear + 1}
          />
        </div>
      </FilterSection>

      {/* Quilometragem */}
      <FilterSection title="Quilometragem" defaultOpen={false}>
        <div className="grid grid-cols-2 gap-2">
          <FilterInput
            label="KM mín."
            type="number"
            value={filters.minMileage}
            onChange={(value) => updateFilter('minMileage', value)}
            placeholder="0"
            min="0"
          />
          <FilterInput
            label="KM máx."
            type="number"
            value={filters.maxMileage}
            onChange={(value) => updateFilter('maxMileage', value)}
            placeholder="200.000"
            min="0"
          />
        </div>
      </FilterSection>

      {/* Especificações */}
      <FilterSection title="Especificações" defaultOpen={false}>
        <FilterSelect
          label="Combustível"
          value={filters.fuelType}
          onChange={(value) => updateFilter('fuelType', value)}
          options={options.fuelTypes}
          placeholder="Todos"
        />
        <FilterSelect
          label="Cor"
          value={filters.color}
          onChange={(value) => updateFilter('color', value)}
          options={options.colors}
          placeholder="Todas"
        />
        <FilterSelect
          label="Carroceria"
          value={filters.type}
          onChange={(value) => updateFilter('type', value)}
          options={options.bodyTypes}
          placeholder="Todas"
        />
        <div className="grid grid-cols-2 gap-2">
          <FilterSelect
            label="Portas"
            value={filters.doors}
            onChange={(value) => updateFilter('doors', value)}
            options={[
              { value: '2', label: '2 Portas' },
              { value: '3', label: '3 Portas' },
              { value: '4', label: '4 Portas' },
              { value: '5', label: '5 Portas' },
            ]}
            placeholder="Todas"
          />
          <FilterInput
            label="Final placa"
            type="text"
            value={filters.plateEnd}
            onChange={(value) => updateFilter('plateEnd', value)}
            placeholder="0–9"
            maxLength={1}
          />
        </div>
        <FilterSelect
          label="Blindagem"
          value={filters.armorLevel}
          onChange={(value) => updateFilter('armorLevel', value)}
          options={options.armorLevels}
          placeholder="Todas"
        />
      </FilterSection>

      {/* Origem */}
      <FilterSection title="Origem" defaultOpen={false}>
        <FilterSelect
          label="Leilão"
          value={filters.isAuction}
          onChange={(value) => updateFilter('isAuction', value)}
          options={[
            { value: 'true', label: 'Sim' },
            { value: 'false', label: 'Não' },
          ]}
          placeholder="Todos"
        />
        <FilterSelect
          label="Categoria Especial"
          value={filters.specialCategory}
          onChange={(value) => updateFilter('specialCategory', value)}
          options={options.specialCategories}
          placeholder="Nenhuma"
        />
      </FilterSection>

      {/* Características */}
      {options.characteristics && options.characteristics.length > 0 && (
        <FilterSection title="Características" defaultOpen={false}>
          <FilterCheckboxGroup
            label=""
            options={options.characteristics}
            selected={filters.characteristics}
            onChange={(value) => updateFilter('characteristics', value)}
          />
        </FilterSection>
      )}
    </div>
  )
}
